import { useMemo, useRef, useState } from 'react';
import { Drawer, KeyValues, StatusChip } from '../../components/data';
import {
  DeleteButton,
  EmployeePicker,
  ErrorBanner,
  Field,
  Loading,
  Modal,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate, formatNumber, todayLocal } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type {
  Employee,
  Me,
  OnboardingDetail,
  OnboardingOption,
  OnboardingTask,
  OnboardingTaskStatus,
  OnboardingTemplate,
} from '../../lib/types';
import {
  answerPayload,
  BLANK_ANSWERS,
  ContactFields,
  LocationFields,
  ServiceFields,
  TechnicalFields,
  type AnswerForm,
} from './OnboardingFields';
import {
  CATEGORIES,
  CATEGORY_GLYPH,
  CATEGORY_LABEL,
  fillPlaceholders,
  formatBytes,
  mailtoHref,
  progressPct,
  TASK_STATUS_LABEL,
  taskBand,
  TIMELINE_LABEL,
} from './onboardingMeta';
import { downloadTemplateFile, saveDraftToMailbox } from './onboardingEmail';
import { DraftSaved } from './OnboardingLibrary';

/**
 * One onboarding: the task board, the answers behind it, and the handover.
 *
 * This is the source app's admin client detail and its client portal, merged.
 * They were two screens because one of them was for the client to log into;
 * with no client login there is one board, and the `internal` category is what
 * marks the rows that must never leave the building — the email composer
 * refuses to open on them, and their notes are shown with that stated.
 */

const STATUS_GROUPS: { status: OnboardingTaskStatus; label: string }[] = [
  { status: 'pending', label: 'To do' },
  { status: 'in_progress', label: 'In progress' },
  { status: 'complete', label: 'Done' },
];

/* The closing review call is the last thing that happens on every onboarding,
   so it sorts last inside its group whatever its due date says. Carried over
   from the source app, which matched on this exact title. */
const CLOSING_TITLE = 'Setup Onboarding Closing review call';

export function OnboardingWorkspace({
  onboardingId,
  employees,
  onBack,
  onChanged,
}: {
  onboardingId: number;
  employees: Employee[];
  onBack: () => void;
  onChanged: () => void;
}) {
  const record = useResource<OnboardingDetail>(`/sales/onboarding/${onboardingId}`);
  const templates = useResource<OnboardingTemplate[]>('/sales/onboarding/templates');
  const options = useResource<OnboardingOption[]>('/sales/onboarding/options');

  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [handover, setHandover] = useState(false);
  const [compose, setCompose] = useState<OnboardingTask | 'summary' | null>(null);
  /* Fetched rather than threaded down from App: this sits three components
     below the page and only one button cares. Same call AM makes, and /me is
     already in the browser's cache by the time anybody reaches this screen. */
  const me = useResource<Me>('/me');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const today = todayLocal();

  const reload = () => {
    record.reload();
    onChanged();
  };

  const handle = (err: unknown) =>
    setError(err instanceof Error ? err.message : 'Could not save that');

  const remove = async () => {
    setDeleting(true);
    setError(null);
    try {
      await api.del(`/sales/onboarding/${onboardingId}`);
      /* Straight back to the list — this record no longer exists, so staying
         on its screen would leave a page describing nothing. */
      onBack();
    } catch (err) {
      setDeleting(false);
      setConfirmDelete(false);
      handle(err);
    }
  };

  const data = record.data;

  const groups = useMemo(() => {
    const tasks = data?.tasks ?? [];
    const rank = (t: OnboardingTask) => (t.Title === CLOSING_TITLE ? 1 : 0);
    return STATUS_GROUPS.map((g) => ({
      ...g,
      items: tasks
        .filter((t) => t.Status === g.status)
        .sort((a, b) => rank(a) - rank(b) || a.SortOrder - b.SortOrder),
    })).filter((g) => g.items.length > 0);
  }, [data]);

  if (record.loading) return <Loading label="Loading the onboarding" />;
  if (record.error) return <ErrorBanner message={record.error} />;
  if (!data) return null;

  const tasks = data.tasks;
  const complete = tasks.filter((t) => t.Status === 'complete').length;
  const overdue = tasks.filter(
    (t) => t.Status !== 'complete' && t.DueDate && t.DueDate.slice(0, 10) < today,
  ).length;
  const pct = progressPct(complete, tasks.length);
  const handedOver = data.Status === 'handed_over';
  const cancelled = data.Status === 'cancelled';
  const outstanding = tasks.length - complete;
  const isAdmin = me.data?.appRole === 'admin';
  /* Uploads hang off the tasks, and the delete cascades through them. Counted
     here so the dialog can say what is actually about to be destroyed rather
     than "this cannot be undone" over an unknown number of signed agreements. */
  const fileCount = tasks.reduce((n, t) => n + t.files.length, 0);

  const elapsedDays = Math.max(
    0,
    Math.round(
      (new Date(handedOver && data.HandedOverUtc ? data.HandedOverUtc : Date.now()).getTime() -
        new Date(data.SubmittedUtc).getTime()) /
        86_400_000,
    ),
  );

  const contactName = [data.ContactFirstName, data.ContactLastName].filter(Boolean).join(' ');
  const companyName = data.AccountName ?? 'Unnamed client';

  const setStatus = (task: OnboardingTask, status: OnboardingTaskStatus) =>
    api
      .put(`/sales/onboarding/tasks/${task.TaskId}`, { status })
      .then(reload)
      .catch(handle);

  /* Stopping an onboarding without destroying it. The record stays — a
     cancelled onboarding still says a client was sold to and did not land,
     which is a fact worth keeping and the one thing deleting cannot preserve.
     Reversible, because the reason for cancelling is usually "they went quiet"
     and clients come back. */
  const setOnboardingStatus = (status: 'in_progress' | 'cancelled') =>
    api
      .put(`/sales/onboarding/${onboardingId}/status`, { status })
      .then(reload)
      .catch(handle);

  return (
    <>
      <div className="row-between wrap mb-4">
        <div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>
            ← All onboardings
          </button>
          <div className="row wrap mt-2">
            <h2>{companyName}</h2>
            {/* The workspace showed no sign of a cancelled onboarding until
                now — the list had a chip and the record it opened did not, so
                the one screen where you act on it was the one that did not say
                so. Handed over is already obvious from the primary button. */}
            {cancelled && <StatusChip band="neutral">Cancelled</StatusChip>}
          </div>
          <p className="small muted mt-2">
            {[
              contactName || null,
              data.ContactEmail,
              data.Industry,
              data.UserCount ? `${data.UserCount} users` : null,
              data.Timeline ? TIMELINE_LABEL[data.Timeline] : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <div className="row wrap">
          <button type="button" className="btn btn-ghost" onClick={() => setEditing(true)}>
            Edit answers
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={!data.ContactEmail}
            title={data.ContactEmail ? undefined : 'No contact email on this onboarding'}
            onClick={() => setCompose('summary')}
          >
            Email the client
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setAdding(true)}>
            Add task
          </button>
          {/* Not offered once handed over: the endpoint refuses that outright,
              because a finished handover has an AM record behind it that
              reopening would contradict. A button that always 409s is worse
              than no button. */}
          {!handedOver && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void setOnboardingStatus(cancelled ? 'in_progress' : 'cancelled')}
            >
              {cancelled ? 'Reopen' : 'Cancel onboarding'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            disabled={handedOver}
            onClick={() => setHandover(true)}
          >
            {handedOver ? 'Handed over' : 'Hand over to AM'}
          </button>
          {/* Admin only, and last, well away from the primary action. Everyone
              else cancels instead — see the note on the endpoint. */}
          {isAdmin && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="grid grid-4 mb-4">
        <div className="stat">
          <div className="label">Progress</div>
          <div className="figure">{pct}%</div>
          <div className="foot">
            {complete} of {tasks.length} tasks done
          </div>
        </div>
        <div className="stat">
          <div className="label">Outstanding</div>
          <div className="figure">{outstanding}</div>
          <div className="foot">still to work</div>
        </div>
        <div className="stat">
          <div className="label">Overdue</div>
          <div className={`figure${overdue > 0 ? ' accent' : ''}`}>{overdue}</div>
          <div className="foot">past the due date</div>
        </div>
        <div className="stat">
          {/* Elapsed days rather than the start date. A date does not fit the
              figure slot without wrapping, and the number that matters is how
              long this has been running — the same measure the tab's
              "time to hand over" reports. */}
          <div className="label">{handedOver ? 'Took' : 'Running for'}</div>
          <div className="figure">{elapsedDays}d</div>
          <div className="foot">
            {handedOver
              ? `handed over ${formatDate(data.HandedOverUtc)}`
              : `started ${formatDate(data.SubmittedUtc)}`}
          </div>
        </div>
      </div>

      {/* The bar the source app had, kept: a percentage is a number, and this
          is the thing somebody actually looks at to see how far in they are. */}
      {tasks.length > 0 && (
        <div className="onb-bar mb-4" aria-hidden="true">
          <span style={{ width: `${pct}%` }} />
        </div>
      )}

      {handedOver && (
        <div className="card mb-4">
          <div className="card-body tight">
            <p className="small">
              <span className="eyebrow">Handed over</span>
            </p>
            <p className="small muted mt-2">
              This client is with Account Management
              {data.HandedOverBy ? `, handed over by ${data.HandedOverBy}` : ''} on{' '}
              {formatDate(data.HandedOverUtc)}. The tasks below stay open for the record; the
              account itself is worked in the Account Management module now.
            </p>
          </div>
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="empty">
          <h3>No tasks yet</h3>
          <p>
            Nothing on the services step pulled a task in. Add them from the library, or correct
            the answers and the rules will assign them.
          </p>
          <button type="button" className="btn btn-primary mt-4" onClick={() => setAdding(true)}>
            Add the first task
          </button>
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.status} className="mb-4">
            <p className="eyebrow">
              {group.label} · {group.items.length}
            </p>
            <div className="onb-tasks mt-2">
              {group.items.map((task) => (
                <TaskRow
                  key={task.TaskId}
                  task={task}
                  today={today}
                  employees={employees}
                  companyName={companyName}
                  firstName={data.ContactFirstName ?? ''}
                  onStatus={(status) => void setStatus(task, status)}
                  onCompose={() => setCompose(task)}
                  onChanged={reload}
                  onError={handle}
                />
              ))}
            </div>
          </section>
        ))
      )}

      <details className="card mt-6">
        <summary className="card-head">
          <h3>What they told us</h3>
        </summary>
        <div className="card-body">
          <KeyValues
            items={[
              ['Company', companyName],
              ['Industry', data.Industry ?? '—'],
              ['Size', data.CompanySize ?? '—'],
              [
                'Address',
                [data.Address, data.City, data.Region, data.PostalCode].filter(Boolean).join(', ') ||
                  '—',
              ],
              ['Contact', contactName || '—'],
              ['Title', data.ContactTitle ?? '—'],
              ['Email', data.ContactEmail ?? '—'],
              ['Phone', data.ContactPhone ?? '—'],
              ['Prefers', data.PreferredContact],
              [
                'Services',
                data.services
                  .map((c) => options.data?.find((o) => o.Code === c && o.Kind === 'service')?.Name ?? c)
                  .join(', ') || '—',
              ],
              ['Backup', data.BackupType ?? '—'],
              ['Users', data.UserCount === null ? '—' : String(data.UserCount)],
              ['Current provider', data.CurrentProvider ?? '—'],
              ['Operating systems', data.operatingSystems.join(', ') || '—'],
              ['Cloud platforms', data.platforms.join(', ') || '—'],
              ['Timeline', data.Timeline ? TIMELINE_LABEL[data.Timeline] ?? data.Timeline : '—'],
              ['Pain points', data.PainPoints ?? '—'],
              ['Service notes', data.ServiceNotes ?? '—'],
            ]}
          />
        </div>
      </details>

      {adding && (
        <AddTaskDrawer
          onboardingId={onboardingId}
          templates={templates.data ?? []}
          assigned={new Set(tasks.map((t) => t.TemplateId).filter((t): t is number => t !== null))}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            reload();
          }}
        />
      )}

      {editing && (
        <AnswersDrawer
          record={data}
          options={options.data ?? []}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
        />
      )}

      {confirmDelete && (
        <Modal
          title="Delete this onboarding?"
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={deleting}
                onClick={() => setConfirmDelete(false)}
              >
                Keep it
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={deleting}
                onClick={() => void remove()}
              >
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </>
          }
        >
          <p>
            <span className="strong">{companyName}</span> and everything recorded against this
            onboarding will be removed. This cannot be undone.
          </p>

          {/* What goes with it, counted. "This cannot be undone" over an
              unknown number of signed agreements is not a warning anybody can
              act on. */}
          <ul className="mt-4">
            <li>
              {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}, including {complete} already
              marked done
            </li>
            <li>
              {fileCount === 0 ? (
                'No uploaded files'
              ) : (
                <span className="strong">
                  {fileCount} uploaded {fileCount === 1 ? 'file' : 'files'} — signed agreements,
                  asset lists and diagrams are stored here and nowhere else
                </span>
              )}
            </li>
            <li>The answers from the intake wizard</li>
          </ul>

          {handedOver && (
            <div className="alert mt-4" role="alert">
              This client has already been handed over to Account Management. Deleting this removes
              the record of how they were onboarded. Their AM record is not touched — they stay a
              client either way.
            </div>
          )}

          {/* Now that Cancel exists, the dialog can point at it. Deleting is
              for a record that should not be there at all; cancelling is for
              work that stopped, and it keeps the fact that a client was sold
              to and did not land. */}
          {!cancelled && (
            <p className="xs subtle mt-4">
              To stop the work without losing the record, close this and use Cancel onboarding
              instead — it keeps everything and can be reopened.
            </p>
          )}
        </Modal>
      )}

      {handover && (
        <HandoverDrawer
          record={data}
          employees={employees}
          outstanding={outstanding}
          onClose={() => setHandover(false)}
          onDone={() => {
            setHandover(false);
            reload();
          }}
        />
      )}

      {compose && (
        <ComposeModal
          task={compose === 'summary' ? null : compose}
          tasks={tasks}
          to={data.ContactEmail ?? ''}
          firstName={data.ContactFirstName ?? ''}
          companyName={companyName}
          onClose={() => setCompose(null)}
        />
      )}
    </>
  );
}

/* =========================================================================
   One task
   ========================================================================= */

function TaskRow({
  task,
  today,
  employees,
  companyName,
  firstName,
  onStatus,
  onCompose,
  onChanged,
  onError,
}: {
  task: OnboardingTask;
  today: string;
  employees: Employee[];
  companyName: string;
  firstName: string;
  onStatus: (status: OnboardingTaskStatus) => void;
  onCompose: () => void;
  onChanged: () => void;
  onError: (err: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  const band = taskBand(task, today);
  const done = task.Status === 'complete';
  const internal = task.Category === 'internal';

  const patch = (body: Record<string, unknown>) => {
    setBusy(true);
    api
      .put(`/sales/onboarding/tasks/${task.TaskId}`, body)
      .then(onChanged)
      .catch(onError)
      .finally(() => setBusy(false));
  };

  const upload = async (chosen: File) => {
    setBusy(true);
    try {
      /* Base64 on a JSON body — the same arrangement as the job description
         library. `result` is a data: URL, so the prefix is stripped here and
         the server never sees it. */
      const content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('Could not read that file'));
        reader.readAsDataURL(chosen);
      });
      await api.post(`/sales/onboarding/tasks/${task.TaskId}/files`, {
        fileName: chosen.name,
        content,
      });
      onChanged();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
      if (file.current) file.current.value = '';
    }
  };

  return (
    <article className={`onb-task${done ? ' done' : ''}`}>
      <div className="onb-task-head">
        <button
          type="button"
          className={`onb-check${done ? ' on' : ''}`}
          disabled={busy}
          aria-pressed={done}
          aria-label={done ? `Reopen ${task.Title}` : `Complete ${task.Title}`}
          onClick={() => onStatus(done ? 'in_progress' : 'complete')}
        >
          {done ? '✓' : ''}
        </button>

        <button type="button" className="onb-task-title" onClick={() => setOpen((o) => !o)}>
          <span className="title">{task.Title}</span>
          <span className="meta">
            <span className="onb-cat">
              <span aria-hidden="true">{CATEGORY_GLYPH[task.Category]}</span>{' '}
              {CATEGORY_LABEL[task.Category]}
            </span>
            {task.DueDate && <span>Due {formatDate(task.DueDate)}</span>}
            {task.OwnerName && <span>{task.OwnerName}</span>}
            {task.files.length > 0 && (
              <span>
                {task.files.length} {task.files.length === 1 ? 'file' : 'files'}
              </span>
            )}
          </span>
        </button>

        <StatusChip band={band}>
          {band === 'critical' && !done ? 'Overdue' : TASK_STATUS_LABEL[task.Status]}
        </StatusChip>
      </div>

      {open && (
        <div className="onb-task-body">
          {task.Description && <p className="small">{task.Description}</p>}

          {task.InternalNotes && (
            <div className="onb-internal">
              <p className="eyebrow">Internal — never sent to the client</p>
              <p className="small mt-2">{task.InternalNotes}</p>
            </div>
          )}

          <div className="form-grid mt-4">
            <Field label="Status">
              <select
                className="select"
                value={task.Status}
                disabled={busy}
                onChange={(e) => onStatus(e.target.value as OnboardingTaskStatus)}
              >
                {STATUS_GROUPS.map((g) => (
                  <option key={g.status} value={g.status}>
                    {g.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Due">
              <input
                className="input"
                type="date"
                value={task.DueDate?.slice(0, 10) ?? ''}
                disabled={busy}
                onChange={(e) => patch({ dueDate: e.target.value || null })}
              />
            </Field>
          </div>

          <Field label="Owner">
            <EmployeePicker
              employees={employees}
              value={task.OwnerEmployeeId}
              onChange={(ownerEmployeeId) => patch({ ownerEmployeeId })}
              placeholder="Unassigned"
            />
          </Field>

          {(task.RequiresUpload || task.files.length > 0) && (
            <div className="onb-files">
              <p className="eyebrow">
                {task.UploadLabel || 'Attachments'}
                {task.RequiresUpload && <span className="subtle"> · required</span>}
              </p>
              {task.files.map((f) => (
                <div key={f.FileId} className="onb-file">
                  <a href={`/api/sales/onboarding/files/${f.FileId}`} target="_blank" rel="noreferrer">
                    {f.FileName}
                  </a>
                  <span className="subtle xs">
                    {formatBytes(f.SizeBytes)} · {formatDate(f.UploadedUtc)}
                    {f.UploadedBy ? ` · ${f.UploadedBy}` : ''}
                  </span>
                  <DeleteButton
                    what={f.FileName}
                    label="Remove"
                    onConfirm={() =>
                      void api
                        .del(`/sales/onboarding/files/${f.FileId}`)
                        .then(onChanged)
                        .catch(onError)
                    }
                  />
                </div>
              ))}
              <input
                ref={file}
                type="file"
                className="mt-2"
                disabled={busy}
                onChange={(e) => {
                  const chosen = e.target.files?.[0];
                  if (chosen) void upload(chosen);
                }}
              />
            </div>
          )}

          <div className="row wrap mt-4">
            {/* Internal tasks have no client-facing side by definition, so the
                composer is not offered on them at all — rather than offered and
                then quietly stripping the notes out of the body. */}
            {!internal && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={onCompose}>
                Compose email
              </button>
            )}
            {internal && (
              <span className="xs subtle">
                Internal only — this one is never emailed to the client.
              </span>
            )}
            <DeleteButton
              what={`the "${task.Title}" task`}
              onConfirm={() =>
                void api
                  .del(`/sales/onboarding/tasks/${task.TaskId}`)
                  .then(onChanged)
                  .catch(onError)
              }
            />
          </div>

          {!internal && task.EmailBody && (
            <p className="xs subtle mt-2">
              A template is set for this task; composing fills in {firstName || 'the contact'} and{' '}
              {companyName}.
            </p>
          )}
        </div>
      )}
    </article>
  );
}

/* =========================================================================
   Add a task
   ========================================================================= */

function AddTaskDrawer({
  onboardingId,
  templates,
  assigned,
  onClose,
  onSaved,
}: {
  onboardingId: number;
  templates: OnboardingTemplate[];
  assigned: Set<number>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState<'library' | 'custom'>('library');
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'setup',
    requiresUpload: false,
    uploadLabel: '',
    dueDate: '',
    internalNotes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(
        `/sales/onboarding/${onboardingId}/tasks`,
        mode === 'library'
          ? { templateId }
          : {
              title: form.title.trim(),
              description: form.description.trim() || null,
              category: form.category,
              requiresUpload: form.requiresUpload,
              uploadLabel: form.uploadLabel.trim() || null,
              dueDate: form.dueDate || null,
              internalNotes: form.internalNotes.trim() || null,
            },
      );
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the task');
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Add a task"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              saving ||
              (mode === 'library' ? templateId === null : form.title.trim().length === 0)
            }
            onClick={() => void save()}
          >
            {saving ? 'Adding…' : 'Add task'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="tabs">
        <button
          type="button"
          className={`tab${mode === 'library' ? ' active' : ''}`}
          onClick={() => setMode('library')}
        >
          From the library
        </button>
        <button
          type="button"
          className={`tab${mode === 'custom' ? ' active' : ''}`}
          onClick={() => setMode('custom')}
        >
          Write one
        </button>
      </div>

      {mode === 'library' ? (
        <div className="onb-picks mt-4">
          {templates.map((t) => (
            <label
              key={t.TemplateId}
              className={`onb-pick${templateId === t.TemplateId ? ' on' : ''}`}
            >
              <input
                type="radio"
                name="template"
                checked={templateId === t.TemplateId}
                onChange={() => setTemplateId(t.TemplateId)}
              />
              <span className="box round" aria-hidden="true">
                {templateId === t.TemplateId ? '●' : ''}
              </span>
              <span>
                <span className="name">
                  {t.Title}
                  {/* Already on this onboarding. Not disabled — a task can
                      legitimately be run twice, for a second site — but it is
                      said, because adding a duplicate by accident is the more
                      likely of the two. */}
                  {assigned.has(t.TemplateId) && <span className="subtle"> · already added</span>}
                </span>
                <span className="desc">
                  {CATEGORY_LABEL[t.Category]} · due in {t.DefaultDueDays}d
                  {t.RequiresUpload ? ' · needs a file' : ''}
                </span>
              </span>
            </label>
          ))}
        </div>
      ) : (
        <div className="mt-4">
          <Field label="Title">
            <input
              className="input"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Port the main number"
              autoFocus
            />
          </Field>
          <Field label="Description">
            <textarea
              className="textarea"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>
          <div className="form-grid">
            <Field label="Category">
              <select
                className="select"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Due">
              <input
                className="input"
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
              />
            </Field>
          </div>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.requiresUpload}
              onChange={(e) => setForm((f) => ({ ...f, requiresUpload: e.target.checked }))}
            />
            This task needs a document attaching
          </label>
          {form.requiresUpload && (
            <Field label="What is being collected" hint="Shown above the attachment list.">
              <input
                className="input"
                value={form.uploadLabel}
                onChange={(e) => setForm((f) => ({ ...f, uploadLabel: e.target.value }))}
                placeholder="e.g. Signed letter of authority (PDF)"
              />
            </Field>
          )}
          <Field label="Internal notes" hint="Never sent to the client.">
            <textarea
              className="textarea"
              rows={3}
              value={form.internalNotes}
              onChange={(e) => setForm((f) => ({ ...f, internalNotes: e.target.value }))}
            />
          </Field>
        </div>
      )}
    </Drawer>
  );
}

/* =========================================================================
   Correcting the answers
   ========================================================================= */

function AnswersDrawer({
  record,
  options,
  onClose,
  onSaved,
}: {
  record: OnboardingDetail;
  options: OnboardingOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<AnswerForm>({
    ...BLANK_ANSWERS,
    companySize: record.CompanySize ?? '',
    address: record.Address ?? '',
    city: record.City ?? '',
    region: record.Region ?? '',
    postalCode: record.PostalCode ?? '',
    contactFirstName: record.ContactFirstName ?? '',
    contactLastName: record.ContactLastName ?? '',
    contactTitle: record.ContactTitle ?? '',
    contactEmail: record.ContactEmail ?? '',
    contactPhone: record.ContactPhone ?? '',
    preferredContact: record.PreferredContact,
    services: record.services,
    backupType: record.BackupType ?? '',
    serviceNotes: record.ServiceNotes ?? '',
    userCount: record.UserCount === null ? '' : String(record.UserCount),
    currentProvider: record.CurrentProvider ?? '',
    operatingSystems: record.operatingSystems,
    platforms: record.platforms,
    painPoints: record.PainPoints ?? '',
    timeline: record.Timeline ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ TasksAdded: number; TasksRemoved: number } | null>(null);

  const set = (patch: Partial<AnswerForm>) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const outcome = await api.put<{ TasksAdded: number; TasksRemoved: number }>(
        `/sales/onboarding/${record.OnboardingId}`,
        answerPayload(form),
      );
      /* Shown rather than closed on, because changing a checkbox here silently
         adds and withdraws tasks — and somebody who has just lost four rows off
         their board deserves to be told that is what happened. */
      setResult(outcome);
      setSaving(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the answers');
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      wide
      onClose={result ? onSaved : onClose}
      title="Edit the answers"
      subtitle={record.AccountName ?? undefined}
      footer={
        result ? (
          <button type="button" className="btn btn-primary" onClick={onSaved}>
            Back to the board
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save answers'}
            </button>
          </>
        )
      }
    >
      {error && <ErrorBanner message={error} />}

      {result ? (
        <div className="card">
          <div className="card-body">
            <h3>Saved</h3>
            <p className="small mt-2">
              {result.TasksAdded === 0 && result.TasksRemoved === 0
                ? 'The answers changed, but the tasks they pull in did not.'
                : [
                    result.TasksAdded > 0 &&
                      `${result.TasksAdded} ${result.TasksAdded === 1 ? 'task was' : 'tasks were'} added`,
                    result.TasksRemoved > 0 &&
                      `${result.TasksRemoved} untouched ${result.TasksRemoved === 1 ? 'task was' : 'tasks were'} withdrawn`,
                  ]
                    .filter(Boolean)
                    .join(', ') + '.'}
            </p>
            <p className="small muted mt-2">
              Only tasks the rules assigned and nobody has started are ever withdrawn. Anything
              added by hand, or already in progress, stays on the board.
            </p>
          </div>
        </div>
      ) : (
        <>
          <p className="sub">
            The company itself is edited on the sales account — this is what the client told us
            during onboarding. Changing the services will add and withdraw tasks accordingly.
          </p>

          <h3 className="mt-6">Contact</h3>
          <ContactFields form={form} set={set} />

          <h3 className="mt-6">Services</h3>
          <ServiceFields form={form} set={set} options={options} />

          <h3 className="mt-6">Environment</h3>
          <TechnicalFields form={form} set={set} options={options} />

          <h3 className="mt-6">Location</h3>
          <LocationFields form={form} set={set} />
        </>
      )}
    </Drawer>
  );
}

/* =========================================================================
   The handover
   ========================================================================= */

function HandoverDrawer({
  record,
  employees,
  outstanding,
  onClose,
  onDone,
}: {
  record: OnboardingDetail;
  employees: Employee[];
  outstanding: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [accountManagerEmployeeId, setAccountManager] = useState<number | null>(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState(
    String(record.OpportunityMrr ?? record.CurrentMrr ?? ''),
  );
  const [contractType, setContractType] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hand = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/sales/onboarding/${record.OnboardingId}/handover`, {
        accountManagerEmployeeId,
        monthlyRevenue: monthlyRevenue.trim() === '' ? null : Number(monthlyRevenue),
        contractType: contractType.trim() || null,
        notes: notes.trim() || null,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not hand the client over');
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Hand over to Account Management"
      subtitle={record.AccountName ?? undefined}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving}
            onClick={() => void hand()}
          >
            {saving ? 'Handing over…' : 'Hand over'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <p className="sub">
        This makes the client real everywhere at once: they become an Account Management client
        against the same company record the sales account already points at, the account stops
        being a prospect, and this onboarding is closed and stamped. It is one action because a
        half-done handover — sales says client, account management has never heard of them — is
        the state nobody notices until the first review is missed.
      </p>

      {outstanding > 0 && (
        <div className="card mt-4">
          <div className="card-body tight">
            <p className="small">
              <span className="eyebrow">Still open</span>
            </p>
            <p className="small muted mt-2">
              {outstanding} {outstanding === 1 ? 'task is' : 'tasks are'} not finished. Handing over
              is still allowed — the work often finishes after the account manager takes it on —
              but the tasks stay here rather than moving with the client.
            </p>
          </div>
        </div>
      )}

      <Field
        label="Account manager"
        hint="Who owns the relationship from here. Can be set later in Account Management, but the point of the handover is that somebody has it."
      >
        <EmployeePicker
          employees={employees}
          value={accountManagerEmployeeId}
          onChange={setAccountManager}
          placeholder="Not assigned yet"
        />
      </Field>

      <div className="form-grid">
        <Field
          label="Monthly revenue"
          hint={
            record.OpportunityMrr !== null
              ? `From the won deal: ${formatNumber(record.OpportunityMrr, 'currency')}/mo`
              : 'What they pay each month from now on.'
          }
        >
          <input
            className="input"
            type="number"
            min={0}
            value={monthlyRevenue}
            onChange={(e) => setMonthlyRevenue(e.target.value)}
          />
        </Field>
        <Field label="Contract type">
          <input
            className="input"
            value={contractType}
            onChange={(e) => setContractType(e.target.value)}
            placeholder="e.g. Managed Services — 36 months"
          />
        </Field>
      </div>

      <Field label="Notes for the account manager">
        <textarea
          className="textarea"
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What is fragile, who to call, what was promised."
        />
      </Field>
    </Drawer>
  );
}

/* =========================================================================
   The email composer
   ========================================================================= */

function ComposeModal({
  task,
  tasks,
  to,
  firstName,
  companyName,
  onClose,
}: {
  /** Null composes the general "here is what happens next" message. */
  task: OnboardingTask | null;
  tasks: OnboardingTask[];
  to: string;
  firstName: string;
  companyName: string;
  onClose: () => void;
}) {
  const values = { firstName, companyName };

  const initialSubject = task
    ? fillPlaceholders(task.EmailSubject || task.Title, values)
    : 'Your onboarding — next steps';

  const initialBody = (() => {
    if (task?.EmailBody) return fillPlaceholders(task.EmailBody, values);
    if (task) {
      const attached = task.emailFiles.length
        ? `\n\nI have attached ${task.emailFiles.map((f) => f.FileName).join(' and ')} — please fill it in and send it back to me.`
        : '';
      return `Hi ${firstName || 'there'},\n\n${task.Description || task.Title}${attached}\n\nLet us know if you have any questions.\n\nBest regards`;
    }
    /* The general message lists what is still outstanding and CLIENT-FACING.
       Internal tasks are filtered out here as well as at the composer — a
       summary mail that tells the client we have not briefed our own service
       desk yet is the one thing this feature must never do. */
    const next = tasks
      .filter((t) => t.Status !== 'complete' && t.Category !== 'internal')
      .slice(0, 5)
      .map((t, i) => `${i + 1}. ${t.Title}`)
      .join('\n');
    return `Hi ${firstName || 'there'},\n\nA quick update on your onboarding.\n\nHere is what is coming up next:\n\n${next}\n\nAnything at all, just reply to this message.\n\nBest regards`;
  })();

  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const attachments = task?.emailFiles ?? [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fail = (err: unknown) => setError(err instanceof Error ? err.message : 'Could not do that');

  const [saved, setSaved] = useState<string | null>(null);
  const saveDraft = () => {
    setBusy(true);
    setError(null);
    saveDraftToMailbox(to, subject, body, attachments)
      .then(setSaved)
      .catch(fail)
      .finally(() => setBusy(false));
  };

  if (saved) return <DraftSaved title={task?.Title ?? 'Onboarding'} link={saved} onClose={onClose} />;

  return (
    <Modal
      title={task ? `Email — ${task.Title}` : 'Email the client'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          {/* An anchor, not a fetch. This app does not send these: they are
              one-to-one messages from a named person at the start of a
              relationship, they get edited before they go, and the reply has to
              land in that person's inbox. */}
          <a
            className={attachments.length ? 'btn btn-ghost' : 'btn btn-primary'}
            href={mailtoHref(to, subject, body)}
            onClick={onClose}
          >
            {attachments.length ? 'Open in mail without the file' : 'Open in mail'}
          </a>
          {attachments.length > 0 && (
            <button type="button" className="btn btn-primary" disabled={busy || !to.trim()} onClick={saveDraft}>
              {busy ? 'Saving…' : 'Save to my Drafts'}
            </button>
          )}
        </>
      }
    >
      <Field label="To">
        <input className="input" value={to} readOnly />
      </Field>
      <Field label="Subject">
        <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </Field>
      <Field label="Message">
        <textarea
          className="textarea"
          rows={14}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      {error && <ErrorBanner message={error} />}
      {attachments.length > 0 && (
        <Field label="Attachments" hint="Kept on the library task, so every client gets the current version.">
          <div className="onb-files">
            {attachments.map((f) => (
              <div key={f.FileId} className="onb-file">
                <button type="button" className="link-button" onClick={() => void downloadTemplateFile(f).catch(fail)}>
                  {f.FileName}
                </button>
                <span className="subtle xs">{formatBytes(f.SizeBytes)}</span>
              </div>
            ))}
          </div>
        </Field>
      )}
      <p className="xs subtle">
        {attachments.length
          ? 'Saves the email, addressed and with the file attached, to the Drafts folder of your own mailbox. Open it there, add your signature under the message, and send. '
          : 'Opens in your mail client with this filled in. '}
        Nothing is sent from here, and nothing is recorded against the client — log it as an
        activity on the account if it matters.
      </p>
    </Modal>
  );
}
